import { useCallback } from 'react'

import { Button, Icon, Chip, Table } from '@circlefin/components'
import { useMoney } from '@circlefin/hooks'
import { useModal } from '@circlefin/modal-router'
import { routes } from '@services/sections/modal/routes'
import { PaymentMethodType } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { WalletPaymentMethod } from '@shared/graphql'

interface LinkedWalletsTableRowProps {
  /**
   * The balances of the wallet to display.
   */
  wallet: WalletPaymentMethod
}

export const LinkedWalletsTableRow: React.FC<LinkedWalletsTableRowProps> = ({
  wallet,
}) => {
  const { t } = useTranslation('settings')
  const { money } = useMoney()
  const { router } = useModal()

  const setWalletDefault = useCallback(() => {
    router.push({
      pathname: routes.account.billing.paymentMethod.setDefault,
      query: {
        type: PaymentMethodType.wallet,
      },
    })
  }, [router])

  return (
    <Table.Body.Row
      className="cursor-pointer"
      data-testid="linked-wallets-table-row"
    >
      <Table.Body.Cell className="w-12 text-neutral-strong type-body-sm">
        <span className="items-center justify-start">
          <Icon name="LinkOutline" size={18} />
        </span>
      </Table.Body.Cell>
      <Table.Body.Cell className="mt-1 w-52 text-neutral-strong type-body-sm">
        <div className="items-start justify-center">
          <div>
            <span className="text-neutral-strong type-body-sm-bold">
              {t('billing.linkedWallets.table.mainWallet')}
            </span>
            <span className="ml-2">
              {t('billing.linkedWallets.table.notice')}
            </span>
          </div>
          <div className="mt-1 whitespace-nowrap text-neutral-subtlest type-body-sm">
            {`${t('billing.linkedWallets.table.availableBalance')} - ${money({
              number: wallet?.balance.amount,
              variant: wallet?.balance.currency,
              options: {
                symbol: true,
                signDisplay: false,
                padded: false,
              },
            })}`}
          </div>
        </div>
      </Table.Body.Cell>
      <Table.Body.Cell className="w-52 items-end justify-end text-neutral-strong type-body-sm">
        <div className="mr-7 flex items-center justify-end">
          {wallet.isDefault ? (
            <Chip className="text-sm" variant="default/info">
              default
            </Chip>
          ) : (
            <Button onClick={setWalletDefault} size="sm" variant="text">
              {t('billing.linkedCards.setDefault')}
            </Button>
          )}
        </div>
      </Table.Body.Cell>
    </Table.Body.Row>
  )
}
